import clsx from 'clsx'
import { DEVICE_TYPE, useDeviceType } from '@/hooks/useDeviceType'

// Components and icons
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { ChevronRight, OverflowMenuHorizontal } from '@/assets/icons'
import { BreadcrumbItem } from './BreadcrumbItem'

// Types
import type { BreadcrumbItemType, BreadcrumbTextSize } from './BreadcrumbItem'

type BreadcrumbsProps = {
  /**
   * Aria label for the breadcrumbs navigation
   * Default: "Breadcrumb navigation"
   */
  ariaLabel?: string
  /**
   * Aria label for the overflow menu button
   * Default: "Show breadcrumb navigation menu"
   */
  overflowMenuAriaLabel?: string
  /**
   * Array of breadcrumb items with label and optional onClick handler
   */
  items: BreadcrumbItemType[]
  /**
   * Maximum number of breadcrumbs to show before truncating.
   * On mobile, truncates at 2 items. On desktop, truncates at this value (default: 5).
   */
  maxItems?: number
  /**
   * Maximum length of the label to display.
   * Default: 20.
   */
  maxLabelLength?: number

  /**
   * Maximum length of the active label to display.
   * Default: 50.
   */
  maxActiveLabelLength?: number

  /**
   * Text size variant for breadcrumb labels.
   * Default: "sm"
   */
  textSize?: BreadcrumbTextSize

  /**
   * When truncating on **desktop only**, keep the first breadcrumb visible and only include
   * intermediate breadcrumbs (excluding first and last) in the overflow menu.
   * Ignored on mobile/tablet (same behavior as `false`).
   *
   * Default: false
   */
  persistFirstItem?: boolean
}

export default function Breadcrumbs({
  ariaLabel = 'Breadcrumb navigation',
  items,
  maxItems = 5,
  maxLabelLength = 20,
  maxActiveLabelLength = 50,
  textSize = 'sm',
  overflowMenuAriaLabel = 'Show breadcrumb navigation menu',
  persistFirstItem = false,
}: BreadcrumbsProps) {
  const deviceType = useDeviceType()

  if (items.length === 0) {
    return null
  }

  const shouldTruncate = deviceType !== DEVICE_TYPE.DESKTOP ? items.length >= 2 : items.length >= maxItems

  const lastIndex = items.length - 1
  const first = items[0]
  const last = items[lastIndex]

  const persistFirstCrumb = persistFirstItem && deviceType === DEVICE_TYPE.DESKTOP
  const hidden = persistFirstCrumb ? items.slice(1, -1) : items.slice(0, -1)
  const shouldUseOverflow = shouldTruncate && hidden.length > 0

  return (
    <nav
      data-ds-component="Breadcrumbs"
      data-ds-text-size={textSize}
      aria-label={ariaLabel}
      className="flex min-w-0 w-full max-w-full items-center gap-1 overflow-x-hidden py-1"
    >
      {shouldUseOverflow ? (
        <div className="flex w-full min-w-0 max-w-full items-center gap-1">
          {persistFirstCrumb && (
            <span className="shrink-0">
              <BreadcrumbItem item={first} maxLabelLength={maxLabelLength} maxActiveLabelLength={maxActiveLabelLength} textSize={textSize} />
            </span>
          )}
          {persistFirstCrumb && <ChevronRight className="text-brand-neutral-600 w-5 h-5" aria-hidden="true" />}

          <span className="shrink-0">
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="cursor-pointer flex items-center rounded-full p-1 hover:bg-brand-neutral-200 [&>svg]:hover:text-brand-neutral-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-brand-500"
                  aria-label={overflowMenuAriaLabel}
                >
                  <OverflowMenuHorizontal className="w-4 h-4 text-brand-neutral-900 transition-colors" aria-hidden="true" />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  className="z-[1002] rounded-md border border-brand-neutral-200 bg-white p-2 text-sm shadow-lg max-w-[260px]"
                  align="start"
                  sideOffset={2}
                  alignOffset={-3}
                >
                  {hidden.map((item) => (
                    <DropdownMenu.Item
                      key={`menu-item-${item.id}`}
                      className="flex items-center px-3 py-2 cursor-pointer rounded text-brand-neutral-700 font-medium hover:bg-brand-neutral-100 hover:text-brand-500 focus:outline-none focus:bg-brand-neutral-100 focus:text-brand-500"
                      onSelect={() => {
                        item.onClick?.()
                      }}
                    >
                      <span className="block max-w-full overflow-hidden text-ellipsis whitespace-nowrap">{item.label}</span>
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </span>

          <ChevronRight className="text-brand-neutral-600 w-5 h-5 shrink-0" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <BreadcrumbItem item={last} isActive maxLabelLength={maxLabelLength} maxActiveLabelLength={maxActiveLabelLength} textSize={textSize} />
          </div>
        </div>
      ) : (
        <>
          {items.map((item, index) => (
            <div key={`breadcrumb-${item.id}`} className={clsx('flex min-w-0 items-center gap-1', index === lastIndex ? 'min-w-0 flex-1' : 'shrink-0')}>
              <BreadcrumbItem
                item={item}
                isActive={index === lastIndex}
                maxLabelLength={maxLabelLength}
                maxActiveLabelLength={maxActiveLabelLength}
                textSize={textSize}
              />
              {index < lastIndex && <ChevronRight className="text-brand-neutral-600 w-5 h-5" aria-hidden="true" />}
            </div>
          ))}
        </>
      )}
    </nav>
  )
}
